import React, { createContext, useState, useContext, useEffect } from 'react';
import { authApi } from '../services/api';
import type { User, UserRole } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ requires2fa: boolean; pendingId?: string }>;
  register: (userData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('auth_token');
    if (token) {
      refreshUser();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<{ requires2fa: boolean; pendingId?: string }> => {
    const response = await authApi.login(email, password);
    if (response.requires_2fa) {
      return { requires2fa: true, pendingId: response.pending_id };
    }
    setUser({
      userId: response.user!.userId,
      email: response.user!.email,
      firstName: response.user!.firstName,
      lastName: response.user!.lastName,
      role: response.user!.role as UserRole,
      email_verified: response.user!.email_verified,
    });
    return { requires2fa: false };
  };

  const register = async (userData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
  }) => {
    // Registration no longer returns a session token — user must verify
    // email first, then log in. This matches the login endpoint's
    // email verification enforcement.
    await authApi.register(userData);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('auth_token');
    }
  };

  const refreshUser = async (): Promise<boolean> => {
    try {
      const response = await authApi.refreshToken();
      setUser({
        userId: response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        role: response.user.role as UserRole,
        email_verified: response.user.email_verified ?? false,
      });
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      localStorage.removeItem('auth_token');
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
